import { element } from './kit.js';
import type { QuestLog } from './quest-log.js';
import { npcName, objectiveLine } from './quest-text.js';
import { uiFormat, uiText } from './text.js';
import './quests.css';

/**
 * The quests under way, always in sight (ADR 0047): each one's name and what
 * is left of it, or who to go back to once it is done, under the top right
 * buttons. A click opens the journal on that quest; the title folds the
 * list away, remembered for this browser.
 */

/** Shown at once; the journal holds the rest. */
const SHOWN = 3;
const FOLDED_KEY = 'shefira.quest-tracker-folded';

export interface QuestTrackerOptions {
  log: QuestLog;
  onOpen: (questId: string) => void;
}

function readFolded(): boolean {
  try {
    return localStorage.getItem(FOLDED_KEY) === '1';
  } catch {
    return false;
  }
}

export class QuestTracker {
  private readonly root = element('aside', 'quest-tracker');
  private readonly toggle = element('button', 'quest-tracker__toggle');
  private readonly list = element('ol', 'quest-tracker__list');
  private folded = readFolded();
  private readonly offLog: () => void;

  constructor(private readonly options: QuestTrackerOptions) {
    this.root.setAttribute('aria-label', uiText('Quêtes en cours'));
    this.toggle.type = 'button';
    this.toggle.addEventListener('click', () => {
      this.folded = !this.folded;
      try {
        localStorage.setItem(FOLDED_KEY, this.folded ? '1' : '0');
      } catch {
        /* folded for this visit only */
      }
      this.render();
    });
    this.root.append(this.toggle, this.list);
    document.body.append(this.root);
    this.offLog = options.log.onChange(() => {
      this.render();
    });
    this.render();
  }

  private render(): void {
    const entries = this.options.log.entries();
    this.root.hidden = entries.length === 0;
    // The chevron is drawn in CSS: the interface font has no arrows.
    this.toggle.textContent = uiFormat('Quêtes ({count})', { count: entries.length });
    this.root.classList.toggle('quest-tracker--folded', this.folded);
    this.toggle.setAttribute('aria-expanded', String(!this.folded));
    this.list.hidden = this.folded;
    this.list.replaceChildren();
    // Those ready to turn in first: the next step is a walk back.
    const sorted = [...entries].sort((a, b) => Number(b.complete) - Number(a.complete));
    for (const entry of sorted.slice(0, SHOWN)) {
      const item = element('li', 'quest-tracker__quest');
      const open = element('button', 'quest-tracker__open');
      open.type = 'button';
      open.append(element('span', 'quest-tracker__name', entry.quest.name));
      const lines = element('ul', 'quest-tracker__objectives');
      if (entry.complete) {
        lines.append(
          element(
            'li',
            'quest-tracker__objective quest-tracker__objective--ready',
            uiFormat('Retourne voir {npc}', { npc: npcName(entry.quest.turnIn.mob) }),
          ),
        );
      } else {
        entry.quest.objectives.forEach((_, index) => {
          const progress = entry.progress[index];
          if (progress !== undefined && progress.value >= progress.target) return;
          lines.append(
            element('li', 'quest-tracker__objective', objectiveLine(entry.quest, index, progress)),
          );
        });
      }
      open.append(lines);
      open.addEventListener('click', () => {
        this.options.onOpen(entry.quest.id);
      });
      item.append(open);
      this.list.append(item);
    }
    if (sorted.length > SHOWN) {
      this.list.append(
        element(
          'li',
          'quest-tracker__more',
          uiFormat('et {count} autre(s)…', { count: sorted.length - SHOWN }),
        ),
      );
    }
  }

  destroy(): void {
    this.offLog();
    this.root.remove();
  }
}
